import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

import Illustration from "@/assets/iconsLegacy/illustration.svg?react"
import { Button } from "@/components/ds/Button"
import { Dialog, DialogContent } from "@/components/ds/Dialog"
import { InputFormField } from "@/components/ds/Input"
import { Typography } from "@/components/ds/Typography"
import { zYear } from "@/components/features/form/utils.ts"
import { useUpdateTenantMutation } from "@/store/TenantAPI.ts"

type TProps = { isUpdating: boolean; onSave: (year: number) => void }

function YearSelectForm({ isUpdating, onSave }: TProps) {
  const { t } = useTranslation()
  const formSchema = z.object({
    reportingYear: zYear()
      .min(2000, { message: "Reporting year cannot be less than 2000" })
      .max(2100, { message: "Reporting year cannot be more than 2100" }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    reValidateMode: "onChange",
    mode: "onChange",
    resolver: zodResolver(formSchema),
  })

  const onSubmit = (data: { reportingYear: number }) => onSave(data.reportingYear)

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <Illustration className="self-center" />
        <div className="flex flex-col gap-3">
          <Typography variant="subtitle" className="mb-2 font-medium">
            {t("whyReportingYear")}
          </Typography>
          <Typography variant="body1" className="text-neutral-text">
            {t("reportingYearText1")}
          </Typography>
          <Typography variant="body1" className="text-neutral-text">
            {t("reportingYearText2")}
          </Typography>
          <Typography variant="body1" className="text-neutral-text">
            {t("reportingYearText3")}
          </Typography>
        </div>
        <InputFormField name={"reportingYear"} />
        <Button className="h-[50px]" disabled={!form.formState.isValid || isUpdating} type="submit">
          {t("start")}
        </Button>
      </form>
    </FormProvider>
  )
}

interface IYearSelectDialogProps {
  isOpen: boolean
  onClose: () => void
}

export default function YearSelectDialog({ isOpen, onClose }: IYearSelectDialogProps) {
  const [updateTenant, { isLoading }] = useUpdateTenantMutation()

  const onSave = async (year: number) => {
    await updateTenant({ reportingYear: Number(year) }).unwrap()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[780px]" closeButton={null}>
        <YearSelectForm onSave={onSave} isUpdating={isLoading} />
      </DialogContent>
    </Dialog>
  )
}
